import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import camelcaseKeys from 'camelcase-keys';
import ContactAPI from 'dashboard/api/contacts';

const CHANNEL_PRIORITY = {
  'Channel::Email': 1,
  'Channel::Whatsapp': 2,
  'Channel::Sms': 3,
  'Channel::TwilioSms': 4,
  'Channel::WebWidget': 5,
  'Channel::Api': 6,
};

export const getTopLastActivity = async (includeContactInboxes = false) => {
  const { data: { contacts } } = await ContactAPI.getTopLastActivity(includeContactInboxes);
  return contacts.map(contact => camelcaseKeys(contact, { deep: true }));
};

export const generateLabelForContactableInboxesList = ({
  name,
  email,
  channelType,
  phoneNumber,
}) => {
  if (channelType === INBOX_TYPES.EMAIL) {
    return `${name} (${email})`;
  }
  if (
    channelType === INBOX_TYPES.TWILIO ||
    channelType === INBOX_TYPES.WHATSAPP
  ) {
    return `${name} (${phoneNumber})`;
  }
  return name;
};

const transformInbox = ({
  name,
  id,
  email,
  channelType,
  phoneNumber,
  medium,
  ...rest
}) => ({
  id,
  icon: getInboxIconByType(channelType, medium, 'line'),
  label: generateLabelForContactableInboxesList({
    name,
    email,
    channelType,
    phoneNumber,
  }),
  action: 'inbox',
  value: id,
  name,
  email,
  phoneNumber,
  channelType,
  ...rest,
});

export const compareInboxes = (a, b) => {
  // Channels that have no priority defined should come at the end.
  const priorityA = CHANNEL_PRIORITY[a.channelType] || 999;
  const priorityB = CHANNEL_PRIORITY[b.channelType] || 999;

  if (priorityA !== priorityB) {
    return priorityA - priorityB;
  }

  const nameA = a.name || '';
  const nameB = b.name || '';
  return nameA.localeCompare(nameB);
};

export const buildContactableInboxesList = contactInboxes => {
  if (!contactInboxes) return [];

  return contactInboxes.map(transformInbox).sort(compareInboxes);
};

export const getCapitalizedNameFromEmail = email => {
  const name = email.match(/^([^@]*)@/)?.[1] || email.split('@')[0];
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const processContactableInboxes = inboxes => {
  return inboxes.map(inbox => ({
    ...inbox.inbox,
    sourceId: inbox.sourceId,
    canReplyFreely: inbox.canReplyFreely,
  }));
};

export const mergeInboxDetails = (inboxesData, inboxesList = []) => {
  if (!inboxesData || !inboxesData.length) {
    return [];
  }

  return inboxesData.map(inboxData => {
    const matchingInbox =
      inboxesList.find(inbox => inbox.id === inboxData.id) || {};
    return {
      ...camelcaseKeys(matchingInbox, { deep: true }),
      ...inboxData,
    };
  });
};

export const prepareAttachmentPayload = (
  attachedFiles,
  directUploadsEnabled
) => {
  const files = [];
  attachedFiles.forEach(attachment => {
    
    if (directUploadsEnabled) {
      // Condição 1: Usa blobSignedId para uploads diretos
      files.push(attachment.blobSignedId || attachment.signed_id || attachment.id);
    } else {
      // ✅ CORREÇÃO: Para mensagens de forward, extrair o signed_id da dataUrl
      if (attachment.dataUrl) {
        const signedId = extractSignedIdFromUrl(attachment.dataUrl);
        if (signedId) {
          files.push(signedId);
        } else {
          // Fallback: usar o ID se não conseguir extrair o signed_id
          files.push(attachment.id);

        }
      } else if (attachment.resource && attachment.resource.file) {
        // Condição 2: Usa attachment.resource.file, se disponível
        files.push(attachment.resource.file);
      } else {
        // Condição 3: Fallback para ID
        files.push(attachment.id);
      }
    }
  });
  

  return files;
};

// A função extractSignedIdFromUrl já existe e está correta:
const extractSignedIdFromUrl = (dataUrl) => {
  const match = dataUrl.match(/blobs\/redirect\/([^\/]+)/);
  return match ? match[1] : null;
};

export const prepareNewMessagePayload = ({
  targetInbox,
  selectedContact,
  message,
  subject,
  ccEmails,
  bccEmails,
  currentUser,
  attachedFiles = [],
  directUploadsEnabled = false,
  forward=false
}) => {
  const payload = {
    inboxId: targetInbox.id,
    sourceId: targetInbox.sourceId,
    contactId: Number(selectedContact.id),
    message: { content: message },
    assigneeId: currentUser.id,
    forward
  };

  if (attachedFiles?.length) {
    payload.files = prepareAttachmentPayload(
      attachedFiles,
      directUploadsEnabled
    );
  }

  if (subject) {
    payload.mailSubject = subject;
  }

  if (ccEmails) {
    payload.message.cc_emails = ccEmails;
  }

  if (bccEmails) {
    payload.message.bcc_emails = bccEmails;
  }

  return payload;
};

export const prepareWhatsAppMessagePayload = ({
  targetInbox,
  selectedContact,
  message,
  templateParams,
  currentUser,
}) => {
  return {
    inboxId: targetInbox.id,
    sourceId: targetInbox.sourceId,
    contactId: selectedContact.id,
    message: { content: message, template_params: templateParams },
    assigneeId: currentUser.id,
  };
};

export const generateContactQuery = ({ keys = ['email'], query }) => {
  return {
    payload: keys.map(key => {
      const filterPayload = {
        attribute_key: key,
        filter_operator: 'contains',
        values: [query],
        attribute_model: 'standard',
      };
      if (keys.findIndex(k => k === key) !== keys.length - 1) {
        filterPayload.query_operator = 'or';
      }
      return filterPayload;
    }),
  };
};

// API Calls

/**
 * Busca contatos recentes, com suporte a paginação completa e deduplicação.
 * @param {Object} options - Opções de busca.
 * @param {number} [options.page=1] - Página inicial para a busca.
 * @param {string} [options.sortAttr='last_activity_at'] - Atributo para ordenação.
 * @returns {Promise<Array>} Lista de contatos filtrados com email ou número de telefone, sem duplicatas.
 */
export const fetchContactsForward = async ({
                                             page = 1,
                                             sortAttr = 'last_activity_at',
                                           } = {}) => {
  try {
    const allContacts = new Map();
    let currentPage = page;
    let hasMore = true;
    let iterationCount = 0;

    while (hasMore) {
      const {
        data: { payload, meta },
      } = await ContactAPI.get(currentPage, sortAttr);


      const camelCasedPayload = camelcaseKeys(payload, { deep: true });

      camelCasedPayload.forEach(contact => {
        if (!allContacts.has(contact.id)) {
          allContacts.set(contact.id, contact);
        }
      });

      hasMore = meta?.has_more_pages || payload.length === 15;
      currentPage += 1;
      iterationCount += 1;

      if (iterationCount >= 1) {
        break;
      }
    }


    return Array.from(allContacts.values()).sort((a, b) => b.last_activity_at - a.last_activity_at);

  } catch (error) {
    console.error('Erro ao buscar contatos recentes:', error);
    return [];
  }
};

export const searchContacts = async ({ keys, query }) => {
  const {
    data: { payload },
  } = await ContactAPI.filter(
    undefined,
    'name',
    generateContactQuery({ keys, query })
  );
  const camelCasedPayload = camelcaseKeys(payload, { deep: true });
  // Filter contacts that have either phone_number or email
  const filteredPayload = camelCasedPayload?.filter(
    contact => contact.phoneNumber || contact.email
  );
  return filteredPayload || [];
};

export const createNewContact = async input => {
  const payload = {
    name: input.startsWith('+')
      ? input.slice(1) // Remove the '+' prefix if it exists
      : getCapitalizedNameFromEmail(input),
    ...(input.startsWith('+') ? { phone_number: input } : { email: input }),
  };

  const {
    data: {
      payload: { contact: newContact },
    },
  } = await ContactAPI.create(payload);

  return camelcaseKeys(newContact, { deep: true });
};

export const fetchContactableInboxes = async contactId => {
  const {
    data: { payload: inboxes = [] },
  } = await ContactAPI.getContactableInboxes(contactId);

  const convertInboxesToCamelKeys = camelcaseKeys(inboxes, { deep: true });

  return processContactableInboxes(convertInboxesToCamelKeys);
};
